import { DirectionsCar, EventAvailable } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { GqlPlannerShiftCompletionStatus } from "~/__generated__";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";

import useFieldworkerCreate from "../api/useFieldworkerCreate";
import useUserGetById from "../api/useUserGetById";
import FieldworkerForm from "../components/FieldworkerForm";
import PasswordUpdateForm from "../components/PasswordUpdateForm";
import UserCourseList from "../components/UserCourseList";
import UserUpdateForm from "../components/UserUpdateForm";

export function UserUpdateScreenBreadcrumb(match: { params: { userId: string } }) {
    const { data } = useUserGetById(match.params.userId);
    if (!data?.firstName || !data?.lastName) return `User`;
    return `${data.firstName} ${data.lastName}`;
}

export default function UserUpdateScreen() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation("user");
    if (!userId) throw Error("No userID param in url");

    const { data, loading, error } = useUserGetById(userId);
    const { fieldworkerCreate: createFieldworker } = useFieldworkerCreate(userId);

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn>
            <GaiaPageComponent title={data ? `${data.firstName} ${data.lastName}` : ""} isLoading={loading}>
                {data && (
                    <Box display="flex" justifyContent="flex-end">
                        <Tooltip title={t("userUpdateScreen.btnViewAvailability")}>
                            <IconButton onClick={() => navigate(`timeoff`)}>
                                <EventAvailable color="success" fontSize="large" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("userUpdateScreen.btnViewShifts")}>
                            <IconButton
                                sx={{ ml: 1 }}
                                onClick={() =>
                                    navigate(`shifts?plannerShiftCompletionStatus=${GqlPlannerShiftCompletionStatus.Approved}`)
                                }
                            >
                                <DirectionsCar color="warning" fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                {data && (
                    <UserUpdateForm
                        user={data}
                        onSubmitted={() => {
                            navigate(-1);
                        }}
                    />
                )}
            </GaiaPageComponent>

            <GaiaPageComponent title={t("userUpdateScreen.fieldworkerTitle")} isLoading={loading}>
                {data?.fieldworker === null && (
                    <Button onClick={async () => await createFieldworker()} color="primary">
                        {t("userUpdateScreen.fieldworkerCreate")}
                    </Button>
                )}
                {data?.fieldworker && (
                    <FieldworkerForm
                        fieldworker={data.fieldworker}
                        userId={userId}
                        onSubmitted={() => {
                            navigate(-1);
                        }}
                    />
                )}
            </GaiaPageComponent>

            <GaiaPageComponent title={t("userUpdateScreen.passwordTitle")}>
                {data && <PasswordUpdateForm user={data} currentPasswordNotRequired />}
            </GaiaPageComponent>

            <GaiaPageComponent title={t("userUpdateScreen.researchRequirementsTitle")} isLoading={loading}>
                <UserCourseList data={data?.userCourses || []} />
            </GaiaPageComponent>
        </GaiaPageLayoutColumn>
    );
}
